<script setup lang="ts">
import { SancareBreadcrumb } from '@sancare/typescript-commons'
import { SancarePagination } from '@sancare/typescript-commons'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import AutomationGroupStayListActions from '@/automation/stay-list/AutomationGroupStayListActions.vue'
import AutomationGroupStayListTable from '@/automation/stay-list/AutomationGroupStayListTable.vue'
import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import { DEFAULTS } from '@/stay-list/storeDefaultState'
import { AutomationGroup, AutomationModeEnum } from '@/store/modules/automation/types'
import { Stay, StayStatusEnum } from '@/store/modules/stay/types'

import MissingAutomationCriteria from './AutomationGroupCriteriaExplanation.vue'
import StayListCriteria from './StayListCriteria.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()

const stay = ref<Stay>(null)

const pageCount = computed<number>(() => store.state.automation.automationStayList.pageCount)
const totalCount = computed<number>(() => store.state.automation.automationStayList.totalCount)
const currentPage = computed<number>(() => store.state.automation.automationStayListParams.currentPage)
const showStatus = computed(() => store.state.automation.automationStayListParams.showStatus)
const automationGroup = computed<AutomationGroup>(() => store.state.automation.editedAutomationGroup)
const { breadcrumbItems } = useBreadcrumb()

watch(() => automationGroup.value?.name, (name) => {
  if (name && route.query.staylistLabel !== name) {
    router.replace({ query: { ...route.query, staylistLabel: name } })
  }
}, { immediate: true })
const automationMode = computed(() => store.state.automation.automationStayListParams.automationMode)
const automationGroupId = computed(() => store.state.automation.automationStayListParams.automationGroupId)
const automationGroupRequest = computed(() => store.state.automation.getAutomationGroupRequest)
const automStayListParams = computed(() => store.state.automation.automationStayListParams)

watch(automationGroupRequest, ({ fetching, ok }: { fetching: boolean, ok: boolean }) => {
  if (fetching || ! ok) {
    return
  }
  let showStatus = DEFAULTS().STAYLIST_STATUS
  if (automationMode.value === AutomationModeEnum.AUTOMATABLE && automationGroup.value.automated) {
    showStatus = [StayStatusEnum.VALIDATED]
  }
  store.commit('setAutomStayListParam', {
    type: 'showStatus', value: showStatus
  })
})

watch([currentPage, automStayListParams], () => {
  refreshStayList()
}, { immediate: true, deep: true })

onMounted(() => {
  store.dispatch('getAutomationGroup', automationGroupId.value)
})

function refreshStayList() {
  if (automationMode.value && automationGroup.value.id) {
    store.dispatch('getAutomationStayList')
    store.dispatch('refreshAutomStayListUrl')
  }
}

function setParameter(type: string, value: string | number) {
  store.commit('setAutomStayListParam', { type, value })
}

function setPage(value: number) {
  setParameter('currentPage', value)
}
function displayStayMissingCriteria(missingStay) {
  stay.value = missingStay
}
</script>

<template>
  <div class="container">
    <sancare-breadcrumb :items="breadcrumbItems" />
    <div class="row align-items-end">
      <h1 class="col-auto text-center">
        <span id="automationStayList_count">
          {{ totalCount }} {{ totalCount > 9999 ? '+' : '' }}
        </span>
        <span class="undertitle d-block">
          séjours
        </span>
      </h1>
      <h1 class="col text-end">
        <span class="undertitle d-block">
          {{ $t('automation.mode.'+automationMode) }}
        </span>
      </h1>
    </div>
    <div>
      <stay-list-criteria
        :automation-mode="automationMode"
        @set-stay-list-param="setParameter"
        @refresh-staylist="refreshStayList"
      />
      <automation-group-stay-list-actions
        @refresh-staylist="refreshStayList"
        @set-stay-list-parameter="setParameter"
      />
      <div class="row">
        <automation-group-stay-list-table
          :class="{
            'col-8 list-with-panel': automationMode === AutomationModeEnum.WAITING || automationMode === AutomationModeEnum.EXPIRED,
            'col-12': automationMode !== AutomationModeEnum.WAITING && automationMode !== AutomationModeEnum.EXPIRED,
          }"
          @display-stay-missing-criteria="displayStayMissingCriteria"
        />
        <div
          v-if="(automationMode === AutomationModeEnum.WAITING || automationMode === AutomationModeEnum.EXPIRED) && stay && automationGroup"
          class="col-4"
        >
          <missing-automation-criteria
            :automation-group="automationGroup"
            :stay="stay"
          />
        </div>
      </div>
      <sancare-pagination
        :current-page="currentPage"
        :page-count="pageCount"
        @set-page="setPage"
      />
    </div>
  </div>
</template>
